import HBIcon from "@/components/HBIcon";
import Image from "@/components/Image";
import { TrackerA } from "@/components/Tracker";
import { IGameplayConfig } from "@/services/ai-gameplay";
import { incrementAiGameplayExposure } from "@/utils/aiGameplayExposure";
import { getEditorUrlByTemplateId } from "@/utils/gdEdit";
import { gdImgSrcPolicy } from "@/utils/imageUrl";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./index.module.less";

interface IAiGameplayCardProps {
  record: IGameplayConfig;
  location: number;
  actualLocation?: number; // 实际在瀑布流中的位置
  columnSize?: number;
}

/**
 * AI玩法卡片 - 用于插入到feed流中
 * 布局：上方图片/视频，下方标题+副标题（上下结构）+右箭头
 */
export default function AiGameplayCard(props: IAiGameplayCardProps) {
  const { record, location, actualLocation, columnSize = 240 } = props;
  const {
    image,
    HBDiscoveryImage,
    HBDiscoveryImageWidth,
    HBDiscoveryImageHeight,
    hoverMedia,
    HBDiscoveryHoverMedia,
    templateId,
    title,
    bg,
  } = record;
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardRef = useRef<HTMLAnchorElement>(null);
  const hasRecordedExposure = useRef(false);

  // 优先使用发现页专用图片，否则使用默认图片
  const displayImage = HBDiscoveryImage || image;

  // 优先使用发现页专用hover媒体，否则使用默认hover媒体
  const displayHoverMedia = HBDiscoveryHoverMedia || hoverMedia;

  // 计算实际显示高度（类似 PinCard 的做法）
  const displayHeight =
    HBDiscoveryImageWidth && HBDiscoveryImageHeight
      ? Math.round(
          (HBDiscoveryImageHeight / HBDiscoveryImageWidth) * columnSize
        )
      : columnSize * 0.75; // 默认 4:3 比例（宽4高3）

  // 从 title 中通过"｜"分割出主标题和副标题
  const [mainTitle, subTitle] = useMemo(() => {
    if (!title) return ["", ""];
    const parts = title.split("｜");
    return [parts[0]?.trim() || "", parts[1]?.trim() || ""];
  }, [title]);

  // 处理视频自动播放逻辑
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !displayHoverMedia?.video) return;

    // 视频加载完成后自动播放
    const handleCanPlay = () => {
      video.play().catch((err) => {
        console.warn("视频自动播放失败:", err);
      });
    };

    video.addEventListener("canplay", handleCanPlay);

    return () => {
      video.removeEventListener("canplay", handleCanPlay);
    };
  }, [displayHoverMedia?.video]);

  // 记录曝光次数：当卡片进入视口时记录一次曝光
  useEffect(() => {
    const card = cardRef.current;
    if (!card || hasRecordedExposure.current || typeof window === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // 当卡片至少50%进入视口时，记录曝光
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            if (!hasRecordedExposure.current && record.id) {
              incrementAiGameplayExposure(record.id);
              hasRecordedExposure.current = true;
              // 记录后断开观察，避免重复记录
              observer.disconnect();
            }
          }
        });
      },
      {
        threshold: 0.5, // 50%可见时触发
      }
    );

    observer.observe(card);

    return () => {
      observer.disconnect();
    };
  }, [record.id]);

  // 判断是否有视频
  const hasVideo =
    displayHoverMedia?.type === "video" && displayHoverMedia.video;

  // 渲染视频内容
  const renderVideo = useMemo(() => {
    if (hasVideo && displayHoverMedia?.video) {
      return (
        <video
          ref={videoRef}
          className={styles.video}
          src={displayHoverMedia.video}
          poster={displayImage}
          muted
          loop
          playsInline
          preload="auto"
          width={columnSize}
          height={displayHeight}
        />
      );
    }
    return null;
  }, [
    hasVideo,
    displayHoverMedia?.video,
    displayImage,
    columnSize,
    displayHeight,
  ]);

  return (
    <TrackerA
      ref={cardRef}
      className={styles.card}
      style={{ width: columnSize }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      href={getEditorUrlByTemplateId(templateId)}
      target="_blank"
      rel="nofollow"
      data-gd-expose="resource_loc_expose"
      data-gd-click="resource_loc_click"
      data-resource-location={actualLocation ?? location}
      data-resource-title={title}
      data-resource-id={record.id}
      data-module-name="AI玩法"
    >
      <div className={styles.media}>
        {hasVideo ? (
          renderVideo
        ) : (
          <Image
            {...gdImgSrcPolicy(displayImage, { displayWidth: 300 })}
            className={styles.image}
            alt={mainTitle}
            width={columnSize}
            height={displayHeight}
          />
        )}
      </div>

      <div className={styles.content} style={{ background: bg }}>
        <div className={styles.textWrapper}>
          <div className={styles.title}>{mainTitle}</div>
          {subTitle && <div className={styles.subtitle}>{subTitle}</div>}
        </div>
        <HBIcon
          type="ic_chevron_right"
          className={`${styles.icon} ${isHovered ? styles.iconHovered : ""}`}
        />
      </div>
    </TrackerA>
  );
}
